import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, Users, Building2, PartyPopper, Wine, DollarSign, Clock, Award, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PageSEO } from '../components/SEO/PageSEO';
import Mixpanel from '../utils/mixpanel';

const venuePageFaqs = [
  {
    question: "How does LetsHost help fill my venue's off-peak and weekend slots?",
    answer: "We match your venue with pre-qualified event hosts looking for private dining, corporate dinners, and cocktail parties. Our clients come with confirmed budgets, guest counts, and event details—specializing in both high-value weekday corporate events and premium weekend celebrations."
  },
  {
    question: "What types of events does LetsHost send to venues?",
    answer: "Our events range from intimate corporate dinners (25-50 guests) to large cocktail parties and full restaurant buyouts (50-250 guests). We curate matches for all days of the week, with a strong focus on premium weekend bookings."
  },
  {
    question: "How much does LetsHost charge venues?",
    answer: "We charge a small referral fee that applies only after a successful booking. Contact us for specific rates."
  },
  {
    question: "Do I have to accept every booking request?",
    answer: "No. We present options to you, and you have full control over which bookings you accept based on your availability and capacity."
  }
];

const FAQItem = ({ faq, isOpen, toggle }) => (
  <div className="border-b border-stone-200 py-4">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center text-left focus:outline-none"
    >
      <span className="text-lg font-semibold text-gray-900 leading-tight pr-4" style={{ fontFamily: "'Playfair Display', serif" }}>
        {faq.question}
      </span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-stone-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-stone-400 flex-shrink-0" />
      )}
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="mt-3 text-stone-600 leading-relaxed text-sm">
            {faq.answer}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const venueTypes = [
  {
    title: "Have a Restaurant?",
    description: "Turn your off-peak hours into profitable events and maximize your prime-time bookings.",
    icon: <Building2 className="w-5 h-5 text-stone-700 stroke-[1.5]" />,
    features: [
      "Get pre-qualified corporate clients",
      "Fill your slow weekday hours",
      "Maximize private dining revenue",
      "Keep full control of your space"
    ]
  },
  {
    title: "Own a Bar or Lounge?",
    description: "Transform quiet hours into vibrant events and boost your weekday revenue.",
    icon: <Wine className="w-5 h-5 text-stone-700 stroke-[1.5]" />,
    features: [
      "Host lucrative corporate happy hours",
      "Fill early evening time slots",
      "Attract high-value group bookings",
      "Increase weekday business"
    ]
  },
  {
    title: "Perfect for Special Events",
    description: "Your venue could be perfect for our clients looking for unique celebration spaces.",
    icon: <PartyPopper className="w-5 h-5 text-stone-700 stroke-[1.5]" />,
    features: [
      "Access vetted birthday planners",
      "Host memorable celebrations",
      "Flexible space utilization",
      "Premium event opportunities"
    ]
  },
  {
    title: "Large Venue Owner?",
    description: "We bring you qualified leads for your bigger spaces and private rooms.",
    icon: <Users className="w-5 h-5 text-stone-700 stroke-[1.5]" />,
    features: [
      "Corporate event inquiries",
      "Pre-qualified large groups",
      "Consistent booking pipeline",
      "Higher value reservations"
    ]
  }
];

export default function Venues() {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);
  const trackEmailClick = (position) => {
    Mixpanel.track('Venue Email Click', { position });
  };

  const faqSchema = [
    {
      question: "How does LetsHost help fill my venue's off-peak hours?",
      answer: "We match your venue with pre-qualified event hosts looking for private dining, corporate dinners, and cocktail parties. Our clients come with confirmed budgets, guest counts, and event details."
    },
    {
      question: "What types of events does LetsHost send to venues?",
      answer: "Our events range from intimate corporate dinners (20-30 guests) to large cocktail parties and full restaurant buyouts (100+ guests). We specialize in weekday events and groups that align with your venue."
    },
    {
      question: "How much does LetsHost charge venues?",
      answer: "We charge a small referral fee that applies only after a successful booking. Contact us for specific rates."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageSEO
        title="List Your Venue and Get More Bookings, Risk Free"
        description="Partner with LetsHost NYC. Fill your excess capacity and boost revenue with qualified event leads."
        path="/venues"
        ogImage="/letshost-share-preview.png"
        faqSchema={faqSchema}
      />

      <Navbar />

      {/* ───────── HERO ───────── */}
      <section className="pb-6 sm:pb-12 md:pb-16 lg:pb-20 px-5 sm:px-10 lg:px-16" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-7xl mx-auto mt-2 sm:mt-6 md:mt-8 lg:mt-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10 lg:gap-16">

            {/* Left — headline + CTAs */}
            <motion.div
              className="w-full lg:w-1/2 flex-shrink-0"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <span className="text-xs tracking-[0.16em] uppercase text-stone-400 font-sans font-medium block mb-5">
                For Venue Partners
              </span>
              <h1
                className="text-[2.75rem] sm:text-[3.5rem] md:text-[4.2rem] lg:text-[3.6rem] xl:text-[4.4rem] font-bold text-gray-900 leading-[1.06] mb-6 max-w-xs sm:max-w-lg"
                style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
              >
                Got a Venue?<br />Let's Fill It.
              </h1>
              <p className="text-base sm:text-lg text-stone-500 leading-relaxed mb-9 max-w-sm">
                We help you fill excess capacity and secure prime-time bookings alike. Most venues categorize our fee as marketing.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <motion.a
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  href="mailto:venues@letshost.nyc?subject=New Venue Signup"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg font-medium text-white bg-stone-900 hover:bg-stone-700 transition-colors text-sm shadow-md"
                  onClick={() => trackEmailClick('hero')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Sign Your Venue Up
                </motion.a>
                <a
                  href="mailto:venues@letshost.nyc"
                  className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg font-medium text-stone-600 border border-stone-300 bg-white hover:bg-stone-50 transition-colors text-sm"
                  onClick={() => trackEmailClick('hero_secondary')}
                >
                  Contact Us
                </a>
              </div>
              <p className="text-sm text-stone-400 mt-4 max-w-xs">
                We're selective about our venue partners to ensure quality matches.
              </p>
            </motion.div>

            {/* Right — value prop cards */}
            <motion.div
              className="w-full lg:w-1/2 relative"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {/* Card 1 */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <Users className="w-5 h-5 text-stone-500 stroke-[1.5]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>Pre-qualified Clients</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Clear intent to book with all details provided upfront</p>
                  </div>
                </div>
              </div>

              {/* Card 2 — offset right */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100 mb-4 ml-6 sm:ml-10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <Clock className="w-5 h-5 text-stone-500 stroke-[1.5]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>Fill Off-Peak Hours</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Turn quiet times into profitable event bookings</p>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100 mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <DollarSign className="w-5 h-5 text-stone-500 stroke-[1.5]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>Maximize Revenue</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Optimize prime-time slots with high-value bookings</p>
                  </div>
                </div>
              </div>

              {/* Card 4 — offset right */}
              <div className="relative z-10 bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-stone-100 ml-6 sm:ml-10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    <Award className="w-5 h-5 text-stone-500 stroke-[1.5]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>Clear Expectations</h3>
                    <p className="text-xs text-stone-500 leading-relaxed">Transparent budgets and guest counts from the start</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ───────── EXAMPLE BOOKINGS ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Real Booking Examples
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-lg mx-auto">
              See how we help venues maximize their booking potential
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Corporate Happy Hour */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-[#FAFAF8] rounded-xl p-7 sm:p-8 border border-stone-100"
            >
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Corporate Happy Hour</h3>
                  <p className="text-sm text-stone-500">Monday, 5–7 PM</p>
                </div>
                <span className="inline-flex items-center bg-white px-3 py-1.5 rounded-full text-sm font-medium text-stone-700 border border-stone-200 shadow-sm">
                  <Users className="w-3.5 h-3.5 mr-1.5" />
                  80 People
                </span>
              </div>
              
              <div className="bg-white p-5 rounded-xl border border-stone-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-stone-500 mb-2">Without LetsHost:</p>
                    <div className="space-y-1">
                      <p className="text-sm flex justify-between">
                        <span>Value:</span> 
                        <span className="text-stone-400">$0</span>
                      </p>
                      <p className="text-sm flex justify-between">
                        <span>Net:</span> 
                        <span className="text-stone-400">$0</span>
                      </p>
                    </div>
                  </div>
                  <div className="border-l border-stone-200 pl-4">
                    <p className="text-sm font-medium text-stone-700 mb-2">With LetsHost:</p>
                    <div className="space-y-1">
                      <p className="text-sm flex justify-between">
                        <span>Value:</span> 
                        <span className="text-stone-900 font-medium">$10,000</span>
                      </p>
                      <p className="text-sm flex justify-between">
                        <span>Net:</span> 
                        <span className="text-emerald-600 font-medium">$8,500</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Saturday Birthday */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#FAFAF8] rounded-xl p-7 sm:p-8 border border-stone-100"
            >
              <div className="flex justify-between items-start mb-5">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Saturday Birthday</h3>
                  <p className="text-sm text-stone-500">Saturday Night</p>
                </div>
                <span className="inline-flex items-center bg-white px-3 py-1.5 rounded-full text-sm font-medium text-stone-700 border border-stone-200 shadow-sm">
                  <Users className="w-3.5 h-3.5 mr-1.5" />
                  50 People
                </span>
              </div>
              
              <div className="bg-white p-5 rounded-xl border border-stone-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-stone-500 mb-2">Revenue:</p>
                    <div className="space-y-1">
                      <p className="text-sm flex justify-between">
                        <span>Value:</span> 
                        <span className="text-stone-900 font-medium">$2,500</span>
                      </p>
                      <p className="text-sm flex justify-between">
                        <span>Net:</span> 
                        <span className="text-emerald-600 font-medium">$2,125</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── VENUE TYPES ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F0EB' }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Perfect For All Venue Types
            </h2>
            <p className="text-base sm:text-lg text-stone-500 max-w-lg mx-auto">
              Whether you have a restaurant, bar, or event space, we can help you maximize bookings
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {venueTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl p-7 sm:p-8 border border-stone-100 shadow-sm"
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5F0EB' }}>
                    {type.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{type.title}</h3>
                    <p className="text-sm text-stone-500 leading-relaxed">{type.description}</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                      <span className="text-sm text-stone-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-5">
              <CheckCircle className="w-10 h-10 text-stone-400 mx-auto" strokeWidth={1.5} />
            </div>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              No Risk, Just Upside.
            </h2>
            <p className="text-base sm:text-lg text-stone-500 mb-9 max-w-lg mx-auto">
              Our fee applies only after an event has been hosted. Turn empty slots into revenue.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="mailto:venues@letshost.nyc?subject=New Venue Signup"
                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-medium text-white bg-stone-900 hover:bg-stone-700 transition-colors text-base shadow-md"
                onClick={() => trackEmailClick('footer')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* ───────── FAQ SECTION ───────── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF8] border-t border-stone-100">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Partner FAQ
            </h2>
            <p className="text-stone-500">
              Everything you need to know about partnering with LetsHost.
            </p>
          </motion.div>

          <div className="space-y-2">
            {venuePageFaqs.map((faq, idx) => (
              <FAQItem 
                key={idx} 
                faq={faq} 
                isOpen={openFaqIndex === idx} 
                toggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} 
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
